import React from 'react'
import {Search, MapPin, Clock, Star} from 'lucide-react'
import { Link } from 'react-router-dom'

function HomePage() {
  const featuredCards = [
    {
      id: 1,
      category: 'Hipster Districts',
      title: 'Shimokitazawa: Vintage Paradise',
      image: 'https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=800&q=80',
      description: 'Dive into Tokyo\'s coolest vintage shops and indie music scene',
      link: '/shimokitazawa',
      accent: '#00E5FF'
    },
    {
      id: 2,
      category: 'Weird Tokyo',
      title: 'Hedgehog Cafes & Animal Encounters',
      image: 'https://images.unsplash.com/photo-1618945524163-32451704651e?w=800&q=80',
      description: 'Meet Tokyo\'s cutest and most unusual animal cafe residents',
      link: '/hedgehog-cafe',
      accent: '#FF006E'
    },
    {
      id: 3,
      category: 'Nightlife',
      title: 'Golden Gai: Tiny Bars, Big Vibes',
      image: 'https://images.unsplash.com/photo-1542051841857-5f90071e7989?w=800&q=80',
      description: 'Navigate Shinjuku\'s legendary alleyway of micro-bars',
      link: '/article/golden-gai',
      accent: '#FF006E'
    },
    {
      id: 4,
      category: 'Logistics',
      title: 'Narita to Tokyo: The Smart Way',
      image: 'https://images.unsplash.com/photo-1564859228273-274232fdb516?w=800&q=80',
      description: 'Save time and money with the best airport transfer options',
      link: '/narita-transfer',
      accent: '#39FF14'
    }
  ]

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="relative h-[85vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1503899036084-c55cdd92da26?w=1600&q=80)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0A0A0A]/50 to-[#0A0A0A]" />
        
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight">
            Discover the Tokyo
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00E5FF] via-[#FF006E] to-[#39FF14]">
              Others Miss
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 font-light">
            Hidden districts, weird attractions, and adventures beyond the guidebooks
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto relative">
            <input
              type="text"
              placeholder="Search hidden gems, weird cafes, nightlife..."
              className="w-full px-6 py-4 bg-[#1A1A1A]/80 backdrop-blur-md text-white rounded-full border-2 border-[#00E5FF]/30 focus:border-[#00E5FF] focus:outline-none placeholder-gray-500 pr-14"
            />
            <button className="absolute right-2 top-1/2 -translate-y-1/2 bg-gradient-to-r from-[#00E5FF] to-[#FF006E] p-3 rounded-full hover:scale-105 transition-transform">
              <Search className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#00E5FF] rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-[#00E5FF] rounded-full" />
          </div>
        </div>
      </section>

      {/* Featured "Best Of" Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="text-4xl font-bold text-white mb-2">Best of Tokyo</h2>
            <p className="text-gray-400 text-lg">Curated experiences for the adventurous traveler</p>
          </div>
          <Star className="w-12 h-12 text-[#FF006E]" fill="#FF006E" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredCards.map((card) => (
            <Link
              key={card.id}
              to={card.link}
              className="group relative overflow-hidden rounded-2xl bg-[#1A1A1A] hover:transform hover:scale-105 transition-all duration-300"
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={card.image}
                  alt={card.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/50 to-transparent" />
                
                <div className="absolute top-4 left-4">
                  <span
                    className="px-3 py-1 text-xs font-bold rounded-full"
                    style={{
                      backgroundColor: card.accent,
                      color: '#0A0A0A',
                      boxShadow: `0 0 20px ${card.accent}80`
                    }}
                  >
                    {card.category}
                  </span>
                </div>
              </div>

              <div className="p-5">
                <h3 className="text-xl font-bold text-white mb-2 group-hover:text-[#00E5FF] transition-colors">
                  {card.title}
                </h3>
                <p className="text-gray-400 text-sm line-clamp-2">
                  {card.description}
                </p>
              </div>

              <div
                className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"
                style={{ backgroundColor: card.accent }}
              />
            </Link>
          ))}
        </div>
      </section>

      {/* Quick Category Links */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Link
            to="/hipster-districts"
            className="group relative p-8 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] rounded-2xl border-2 border-[#00E5FF]/20 hover:border-[#00E5FF] transition-all overflow-hidden"
          >
            <MapPin className="w-10 h-10 text-[#00E5FF] mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Hipster Districts</h3>
            <p className="text-gray-400">Vintage shops, indie music, local vibes</p>
            <div className="absolute inset-0 bg-gradient-to-br from-[#00E5FF]/0 to-[#00E5FF]/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>

          <Link
            to="/weird-tokyo"
            className="group relative p-8 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] rounded-2xl border-2 border-[#FF006E]/20 hover:border-[#FF006E] transition-all overflow-hidden"
          >
            <Star className="w-10 h-10 text-[#FF006E] mb-4" fill="#FF006E" />
            <h3 className="text-2xl font-bold text-white mb-2">Weird Tokyo</h3>
            <p className="text-gray-400">Animal cafes, robots, unique attractions</p>
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF006E]/0 to-[#FF006E]/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>

          <Link
            to="/logistics"
            className="group relative p-8 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] rounded-2xl border-2 border-white/20 hover:border-white transition-all overflow-hidden"
          >
            <Clock className="w-10 h-10 text-white mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">Logistics</h3>
            <p className="text-gray-400">Transport, JR Pass, practical guides</p>
            <div className="absolute inset-0 bg-gradient-to-br from-white/0 to-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>

          <Link
            to="/nightlife"
            className="group relative p-8 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] rounded-2xl border-2 border-[#FF006E]/20 hover:border-[#FF006E] transition-all overflow-hidden"
          >
            <div className="text-4xl mb-4">🌃</div>
            <h3 className="text-2xl font-bold text-white mb-2">Nightlife</h3>
            <p className="text-gray-400">Golden Gai, Roppongi, late-night eats</p>
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF006E]/0 to-[#FF006E]/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] border-t border-[#00E5FF]/20 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">
            © 2024 Footprint Tokyo. Discover the Tokyo others miss.
          </p>
        </div>
      </footer>
    </div>
  )
}

export default HomePage
